import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect } from 'react';
import useDebounce from './useDebounce';
import { VIRTUALIZER_ESTIMATE_SIZE, VIRTUALIZER_OVERSCAN } from '@constants/constants';

interface UseTanstackVirtualizerProps<TData> {
  rows: TData[];
  parentRef: React.RefObject<HTMLDivElement>;
  itemHeight?: number;
  overscan?: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

const useTanstackVirtualizer = <TData>(props: UseTanstackVirtualizerProps<TData>) => {
  const { rows, parentRef, itemHeight, overscan, hasNextPage, isFetchingNextPage, fetchNextPage } =
    props;

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? rows.length + 1 : rows.length,
    getScrollElement: () => parentRef.current || null,
    estimateSize: () => itemHeight ?? VIRTUALIZER_ESTIMATE_SIZE,
    overscan: overscan ?? VIRTUALIZER_OVERSCAN,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const debouncedFetch = useDebounce(() => {
    const [lastItem] = [...virtualItems].reverse();
    if (!lastItem) {
      return;
    }

    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    if (lastItem.index >= rows.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, 300);

  useEffect(() => {
    debouncedFetch();
  }, [debouncedFetch]);

  return { rowVirtualizer };
};

export default useTanstackVirtualizer;
